import React, { useMemo } from 'react'

import { Box, Display, Heading5, Space, Visible } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import {
  RecipeTile,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type { MenuPreferences } from '@library/api-menu-experience'
import type { MenuRecipeTileRequestParams, Recipe, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { FlexSearchRecipeTile } from 'routes/Menu/components/Search/SearchRecipeTile/FlexSearchRecipeTile'
import type { AddRecipeByUuidFn, RemoveRecipeByUuidFn } from 'routes/Menu/domains/basket'
import { CollectionId } from 'routes/Menu/domains/collections'
import { useCollectionRecipes } from 'routes/Menu/domains/menu'
import { useRecipeTagsCwcWeb } from 'routes/Menu/hooks/useRecipeTagsCwcWeb'
import { getIsAuthenticated } from 'selectors/auth'
import {
  getBasketRecipesCount,
  getBasketMenuUuid,
  getBasketRecipeNumPortionsForId,
  getBasketRecipeQuantityForId,
} from 'selectors/basket'
import { getUserFirstName } from 'selectors/user'

import type { SearchRecipeTileTrackingData, TrackCTAClick } from '../types'

type SuggestedRecipeProps = {
  addRecipe: AddRecipeByUuidFn
  canAddRecipes: boolean
  menuPreferences?: MenuPreferences
  onClick: (recipeId: RecipeUUID) => void
  onEditFlexPortions?: (recipe: Recipe, trackingData: SearchRecipeTileTrackingData) => void
  onUpdateRecipePortions?: (
    recipeId: RecipeUUID,
    numPortions: MenuRecipeTileRequestParams['numPortions'],
  ) => void
  reachedLimit: boolean
  removeRecipe: RemoveRecipeByUuidFn
}

export const SuggestedRecipe = ({
  addRecipe,
  canAddRecipes,
  menuPreferences,
  onClick,
  onEditFlexPortions,
  onUpdateRecipePortions,
  reachedLimit,
  removeRecipe,
}: SuggestedRecipeProps) => {
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isRecipeTagsCwcEnabled = useRecipeTagsCwcWeb()
  const currentCollectionId = useCurrentCollectionId()

  const userFirstName = useSelector(getUserFirstName)
  const menuId = useSelector(getBasketMenuUuid)

  const recommendationsRecipes = useCollectionRecipes(CollectionId.Recommendations)
  const recipe = useMemo(() => {
    if (recommendationsRecipes.length === 0) {
      return null
    }

    const [firstRecipe] = recommendationsRecipes

    return firstRecipe.recipe
  }, [recommendationsRecipes])

  const id = recipe?.id ?? null
  const quantity = useSelector(getBasketRecipeQuantityForId(id))
  const recipeNumPortions = useSelector(getBasketRecipeNumPortionsForId(id))
  const basketRecipeCount = useSelector(getBasketRecipesCount)

  if (!isAuthenticated || !recipe) return null

  const flexPortionTrackingContext = {
    basketRecipeCount,
    menuUuid: menuId,
    recipePortionCount: recipeNumPortions,
    recipeUuid: recipe.id,
    source: 'menu_search_screen' as const,
  }

  return (
    <Box>
      <Heading5>Suggested For {userFirstName}</Heading5>
      <Space size={4} />
      <Visible display={[Display.None, Display.Block]}>
        <FlexSearchRecipeTile
          addRecipe={(recipeId) => addRecipe(recipeId, 'menu_search_screen')}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={isAuthenticated}
          onClick={() => onClick(recipe.id)}
          reachedLimit={reachedLimit}
          recipe={recipe}
          removeRecipe={removeRecipe}
          onEditFlexPortions={onEditFlexPortions}
          trackingData={{ menuId }}
        />
      </Visible>
      <Visible display={[Display.Block, Display.None]}>
        <RecipeTile
          recipe={recipe}
          addRecipe={(recipeId) => {
            queueTrackingEvent<TrackCTAClick>({
              action: 'add_recipe',
              properties: {
                menu_id: menuId,
                add_type: 'search',
                recipe_id: recipe.id,
                event_screen: 'menu_search_screen',
              },
            })
            addRecipe(recipeId, 'menu_search_screen')
          }}
          alternativeOptions={{ optionsType: 'none', options: [] }}
          canAddRecipes={canAddRecipes}
          currentCollectionId={currentCollectionId}
          menuId={menuId}
          isInBasket={quantity > 0}
          isOutOfStock={false}
          onClick={() => onClick(recipe.id)}
          originalId={recipe.id}
          reachedLimit={reachedLimit}
          recipeReference=""
          removeRecipe={(recipeId) => {
            queueTrackingEvent<TrackCTAClick>({
              action: 'remove_recipe',
              properties: {
                menu_id: menuId,
                add_type: 'search',
                recipe_id: recipe.id,
                event_screen: 'menu_search_screen',
              },
            })
            removeRecipe(recipeId)
          }}
          selectVariant={() => {}}
          setBrowserCTAVisible={() => {}}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={isAuthenticated}
          showFlexPortionTileBadge
          isRecipeTagsCwcEnabled={isRecipeTagsCwcEnabled}
          insideOverflow
          numPortions={recipeNumPortions}
          quantity={quantity}
          onClickEditPortions={() => trackClickEditRecipeQuantity(flexPortionTrackingContext)}
          onSelectPortions={(numPortions) =>
            trackSelectPortionCount(flexPortionTrackingContext, numPortions)
          }
          onUpdateRecipePortions={(recipeId, updatedNumPortions) => {
            onUpdateRecipePortions?.(recipeId, updatedNumPortions)
            trackUpdatePortion(flexPortionTrackingContext, updatedNumPortions)
          }}
        />
      </Visible>
    </Box>
  )
}
